import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { logAudit, AUDIT_ACTIONS } from '../../utils/audit';
import { generateAssetHistoryPDF } from '../../utils/pdf';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconFileTypePdf,
} from '@tabler/icons-react';

export default function AssetDetail() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, org } = useAuth();
  const [asset, setAsset] = useState(null);
  const [checks, setChecks] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [unblockReason, setUnblockReason] = useState('');
  const [unblocking, setUnblocking] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const branchId = userProfile?.branchId;
  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId || !branchId) {
        setLoading(false);
        return;
      }
      try {
        const [assetSnap, checksSnap, incidentsSnap] = await Promise.all([
          getDoc(doc(db, 'organisations', resolvedOrgId, 'branches', branchId, 'assets', assetId)),
          getDocs(query(
            collection(db, 'organisations', resolvedOrgId, 'branches', branchId, 'checkRecords'),
            orderBy('submittedAt', 'desc')
          )),
          getDocs(collection(db, 'organisations', resolvedOrgId, 'incidents')),
        ]);

        if (assetSnap.exists()) {
          setAsset({ id: assetSnap.id, branchId, ...assetSnap.data() });
        }

        const allChecks = checksSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.assetId === assetId || c.assetReg === assetSnap.data()?.reg);
        setChecks(allChecks);

        const allIncidents = incidentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(i => i.assetId === assetId || i.assetReg === assetSnap.data()?.reg);
        setIncidents(allIncidents);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [assetId, resolvedOrgId, branchId]);

  const handleUnblock = async () => {
    if (!unblockReason.trim()) return;
    setUnblocking(true);
    try {
      await updateDoc(
        doc(db, 'organisations', resolvedOrgId, 'branches', branchId, 'assets', assetId),
        {
          isBlocked: false,
          blockedReason: null,
          unblockReason,
          unblockedAt: serverTimestamp(),
          unblockedBy: userProfile?.name || 'Unknown',
        }
      );

      await logAudit(resolvedOrgId, {
        action: AUDIT_ACTIONS.ASSET_UNBLOCKED,
        performedBy: userProfile?.id,
        performedByName: userProfile?.name,
        targetId: assetId,
        targetType: 'asset',
        targetName: `${asset.make} ${asset.model}${asset.reg ? ` · ${asset.reg}` : ''}`,
        details: `Unblocked — ${unblockReason}`,
        branchId,
      });

      setAsset(prev => ({ ...prev, isBlocked: false, blockedReason: null }));
      setShowUnblockModal(false);
      setUnblockReason('');
    } catch (err) {
      console.error(err);
    }
    setUnblocking(false);
  };

  const handleExportPDF = async () => {
    if (!asset) return;
    setExportingPdf(true);
    try {
      generateAssetHistoryPDF(asset, checks, incidents, org);
    } catch (err) {
      console.error(err);
    }
    setExportingPdf(false);
  };

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
    </Layout>
  );

  if (!asset) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/assets')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Assets
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Asset not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/assets')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Assets
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 6 }}>{asset.make} {asset.model}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {asset.reg && (
                <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#FAC775', color: '#412402', padding: '2px 8px', borderRadius: 4 }}>{asset.reg}</span>
              )}
              {asset.vin && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-tertiary)' }}>VIN: {asset.vin}</span>
              )}
              {asset.serialNumber && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-tertiary)' }}>S/N: {asset.serialNumber}</span>
              )}
              {asset.isBlocked && (
                <span style={{ fontSize: 11, background: 'var(--color-danger-light)', color: 'var(--color-danger)', padding: '2px 8px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <IconX size={11} stroke={2} /> Blocked
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleExportPDF}
              disabled={exportingPdf}
              style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '8px 14px', fontSize: 13, cursor: exportingPdf ? 'not-allowed' : 'pointer', opacity: exportingPdf ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <IconFileTypePdf size={14} stroke={1.5} /> {exportingPdf ? 'Generating...' : 'Export PDF'}
            </button>
            {asset.isBlocked && (
              <button
                onClick={() => setShowUnblockModal(true)}
                style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
              >
                Unblock asset
              </button>
            )}
          </div>
        </div>

        {asset.isBlocked && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={18} color="var(--color-danger)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>Asset blocked — {asset.blockedReason}</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Asset details</div>
            {[
              { label: 'Make', value: asset.make },
              { label: 'Model', value: asset.model },
              { label: 'Type', value: asset.type },
              asset.year && { label: 'Year', value: asset.year },
              asset.colour && { label: 'Colour', value: asset.colour },
              asset.fuel && { label: 'Fuel type', value: asset.fuel },
              { label: 'Assigned to', value: asset.isPool ? 'Pool asset' : (asset.assignedTo || 'Unassigned') },
              asset.mileage && { label: 'Current mileage', value: asset.mileage.toLocaleString() + ' mi' },
              asset.vin && { label: 'VIN', value: asset.vin },
              asset.serialNumber && { label: 'Serial number', value: asset.serialNumber },
              asset.departmentId && { label: 'Department', value: asset.departmentId },
            ].filter(Boolean).map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)', fontFamily: row.label === 'VIN' || row.label === 'Serial number' ? 'monospace' : 'inherit' }}>{row.value || '—'}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Compliance</div>
              {[
                { label: 'MOT expiry', value: asset.mot || 'Not recorded', ok: !!asset.mot },
                { label: 'Tax expiry', value: asset.tax || 'Not recorded', ok: !!asset.tax },
                { label: 'Status', value: asset.isBlocked ? 'Blocked' : 'Available', ok: !asset.isBlocked },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{row.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
                    {row.ok ? <IconCheck size={14} color="var(--color-success)" stroke={2} /> : <IconX size={14} color="var(--color-danger)" stroke={2} />}
                  </div>
                </div>
              ))}
            </div>

            {/* Recent checks summary */}
            {checks.length > 0 && (
              <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Recent checks</div>
                {checks.slice(0, 4).map((check, i) => {
                  const date = check.submittedAt?.toDate ? check.submittedAt.toDate() : new Date(check.submittedAt || 0);
                  return (
                    <div key={check.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 3 ? '0.5px solid var(--color-border-light)' : 'none' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: check.result === 'pass' ? 'var(--color-success)' : 'var(--color-danger)', flexShrink: 0 }} />
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)' }}>{check.submittedByName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: check.result === 'pass' ? 'var(--color-success-light)' : 'var(--color-danger-light)', color: check.result === 'pass' ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 600 }}>
                        {check.result?.toUpperCase()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {asset.isBlocked && asset.blockedReason && (
              <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10, color: 'var(--color-danger)' }}>Blocked reason</div>
                <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>{asset.blockedReason}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showUnblockModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-xl)', padding: 28, width: 440, border: '0.5px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 400 }}>Unblock asset</div>
              <div onClick={() => setShowUnblockModal(false)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: 20 }}>×</div>
            </div>
            <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--color-danger)' }}>
              This action is permanent and will be recorded in the audit log. The asset will be immediately available for use.
            </div>
            <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Blocked reason: <strong style={{ color: 'var(--color-text-primary)' }}>{asset.blockedReason}</strong>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Reason for unblocking <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <textarea
                placeholder="e.g. Tyre replaced and pressure checked — asset safe to use"
                value={unblockReason}
                onChange={e => setUnblockReason(e.target.value)}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                This reason will be permanently recorded against the asset
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowUnblockModal(false)} style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={handleUnblock}
                disabled={unblocking || !unblockReason.trim()}
                style={{ flex: 2, background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px', fontSize: 13, cursor: unblocking || !unblockReason.trim() ? 'not-allowed' : 'pointer', opacity: unblocking || !unblockReason.trim() ? 0.5 : 1 }}
              >
                {unblocking ? 'Unblocking...' : 'Confirm unblock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}